import { preventPopoverCloseOnSpacebarPress, stopPropagation } from '@/_shared/tools/event-prevention.js';
import { Button, Input } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import type { Column } from '@tanstack/react-table';
import { type ChangeEvent, useCallback, useRef } from 'react';
import { resetFilter } from '../../_shared/reset.js';
import { useSharedStyles } from '../../shared-styles.js';
import { typedSetStringColumnFilterValue } from '../filter/index.js';
import type { StringFilterValuesWithoutInArray } from '../filter/operation.js';
import type { StringFilterSchemaWithoutInArray } from '../filter/schema.js';

type StringInputProps<TData, TValue> = {
    column: Column<TData, TValue>;
    dropdownValue: StringFilterValuesWithoutInArray['id'];
};

export function StringFilter<TData, TValue>({ column, dropdownValue }: StringInputProps<TData, TValue>) {
    const sharedStyles = useSharedStyles();

    const filter = column.getFilterValue() as StringFilterSchemaWithoutInArray | undefined;
    const value = filter && 'value' in filter ? (filter.value ?? '') : '';

    const inputRef = useRef<HTMLInputElement>(null);

    const onValueChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;

            if (value.length) {
                typedSetStringColumnFilterValue(column, {
                    operation: dropdownValue,
                    value,
                });
            } else {
                resetFilter(column);
            }
        },
        [column, dropdownValue]
    );

    const clearValueInput = useCallback(() => {
        resetFilter(column);
        inputRef.current?.focus();
    }, [column]);

    return (
        <Input
            ref={inputRef}
            className={sharedStyles.input}
            placeholder="Filtern..."
            value={value}
            onChange={onValueChange}
            onMouseDown={stopPropagation}
            onKeyDown={preventPopoverCloseOnSpacebarPress}
            contentAfter={
                <Button appearance="subtle" size="small" icon={<DismissRegular />} onClick={clearValueInput} />
            }
        />
    );
}
